import type Luminary from '@lumen5/luminary';
import { checkpointVideo } from 'js/src/data/video-data/video-data-actions';
import uuid from 'js/src/libs/uuid';
import {
    copySceneContentForAnimatedZoomOperation,
    getAnimatedZoomMediaAsset,
    getAnimatedZoomOverlaySourceOperation,
} from 'js/src/luminary/animated-zoom-scene-options';
import { saveRawLuminary } from 'js/src/luminary/utils';
import {
    createLuminaryOperation,
    type LuminaryOperationContext,
} from 'js/src/luminary/luminary-hooks';
import {
    moveOtherOverlaysAndCreateOverlayOperation,
    setUpMediaOnlyOverlayOperation,
} from 'js/src/luminary/luminary-operations/core';
import { getSceneWithBaseMediaAsset } from 'js/src/luminary/luminary-selectors';
import { mediaAssetsMatchAcrossGap } from 'js/src/luminary/morph-utils';
import {
    enableMorphFrom,
    runVoicedrivenTranscriptBatchOperation,
    sameGroupNextScene,
    sameGroupPrevScene,
} from 'js/src/luminary/voicedriven-scenes';

export type AnimatedZoomTarget = {
    sceneId: string;
    sceneGroupId: string;
    assetId: string;
};

const armMorphEdgeOperation = createLuminaryOperation(({ luminary }, fromSceneId: string) => {
    const from = luminary.getSceneById(fromSceneId);
    const to = from ? sameGroupNextScene(from) : null;
    // Deliberately not `canCarryMorph`, which only recognises a window onto the scene group's base
    // media: an overlay showing its own footage morphs into its duplicate just as well. This is the
    // same test the transitions panel uses to decide the option exists at all.
    if (!from || !to || !mediaAssetsMatchAcrossGap(from, to)) {
        return;
    }
    enableMorphFrom(from);
    to.customTransition.transitionUrl = null;
});

/**
 * Morph-links a voicedriven scene to whichever of its neighbours can carry the morph, so the zoom
 * runs into the scene from before it, back out into the one after it, or both.
 */
const armAnimatedZoomOperation = createLuminaryOperation(({ luminary, call }, sceneId: string) => {
    const sceneGroup = luminary.getSceneById(sceneId)?.sceneGroup;
    if (!sceneGroup) {
        return;
    }
    call(runVoicedrivenTranscriptBatchOperation, sceneGroup.id, () => {
        const scene = luminary.getSceneById(sceneId);
        const prev = scene ? sameGroupPrevScene(scene) : null;
        if (prev) {
            call(armMorphEdgeOperation, prev.id);
        }
        call(armMorphEdgeOperation, sceneId);
    });
});

/**
 * Adds an overlay that repeats what is already on screen, morph-linked to it, so cropping the new
 * scene animates as a zoom or pan rather than cutting.
 */
export const createAnimatedZoomOverlayOperation = createLuminaryOperation(
    (
        { luminary, dispatch, call }: LuminaryOperationContext<Luminary>,
        {
            sceneGroupId,
            transcriptStartTime,
            transcriptEndTime,
            systemTriggered = false,
            parentActionGroup = uuid(),
        }: {
            sceneGroupId: string;
            transcriptStartTime: number;
            transcriptEndTime: number;
            systemTriggered?: boolean;
            parentActionGroup?: string;
        },
        sourceVideoTime: number,
    ): AnimatedZoomTarget | null => {
        const sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup) {
            return null;
        }

        const source = call(getAnimatedZoomOverlaySourceOperation, sceneGroupId, sourceVideoTime);
        if (!source.sceneDesignId) {
            return null;
        }
        // Serialized, not held by reference: creating the overlay moves the overlays around it,
        // which can resize or delete the very scene being duplicated.
        const savedSourceScene = source.scene?.save() ?? null;
        const sceneWithBaseMediaAsset = getSceneWithBaseMediaAsset(sceneGroup);

        const newSceneId = call(
            moveOtherOverlaysAndCreateOverlayOperation,
            sceneGroupId,
            transcriptStartTime,
            transcriptEndTime,
            source.sceneDesignId,
            systemTriggered,
            parentActionGroup,
        );
        const newScene = newSceneId ? luminary.getSceneById(newSceneId) : null;
        if (!newSceneId || !newScene) {
            return null;
        }

        luminary.batchRun((safeLuminary) => {
            if (savedSourceScene) {
                call(copySceneContentForAnimatedZoomOperation, savedSourceScene, newSceneId, true, {
                    luminary: safeLuminary,
                });
            } else {
                call(setUpMediaOnlyOverlayOperation, newSceneId, sceneWithBaseMediaAsset, {
                    luminary: safeLuminary,
                });
            }
        });

        call(armAnimatedZoomOperation, newSceneId);

        dispatch(
            checkpointVideo({
                video_data: saveRawLuminary(luminary),
                systemTriggered,
                parentActionGroup,
            }),
        );

        const assetId = getAnimatedZoomMediaAsset(newScene)?.id;
        return assetId ? { sceneId: newSceneId, sceneGroupId, assetId } : null;
    },
);
